/**
 * Pure classifiers for the outcome of a sign-out (ENG-2907, CONTRACT v2).
 *
 * `signOut()` (client.ts) historically returned `Promise<void>` and inspected
 * neither `res.status` nor `res.ok`, so a failed /logout was indistinguishable
 * from success: the UI reported "signed out" while the server session survived.
 * This module turns the two wire observations a sign-out produces into a
 * structured `SignOutResult` the caller can act on:
 *
 *   1. `classifySignOutResponse` — SYNCHRONOUS, STATUS-ONLY. Maps a completed
 *      POST /logout `Response` (or the fetch rejection standing in for one) onto
 *      a `SignOutResult`. Runs BEFORE the local session wipe, so it must return
 *      without awaiting a body read; it never touches the body.
 *
 *   2. `classifyProbeResponse` — ASYNC, reads the body. Maps a GET /userinfo
 *      confirmation-probe response onto a `SignOutProbeClassification`. Runs
 *      AFTER the local wipe (see the asymmetry note on the function), so it can
 *      afford to `await response.json()`, and it MUST, because the response body
 *      is the only thing that distinguishes proof-of-live from a captive portal.
 *
 * WHAT SHIPS TODAY vs LATER. As narrowed, the T3 caller (client.ts) uses ONLY
 * `classifySignOutResponse`: it owns the POST /logout fetch, then returns that
 * classifier's result unchanged, always emitting `probe: 'not_run'`. It never
 * calls `classifyProbeResponse` and never assembles a probe-derived verdict.
 * `classifyProbeResponse` / `SignOutProbeClassification` are contract-first —
 * present and tested, but with ZERO callers until T8 wires the GET /userinfo
 * confirmation probe and the four-branch assembly below. Both are unsafe before
 * T8's AuthProvider latch (a probe is cancelled by the redirect the null
 * session emission triggers; a kept-alive SPA can re-mint the wiped credential),
 * which is why they wait for it.
 */

/**
 * Why a sign-out did not end the server session.
 */
export type SignOutFailureReason =
  // Fetch rejected before any HTTP status existed: offline, DNS failure, the
  // CORS-denied 403 at services/auth-service/src/handler.ts:206-212 that the
  // browser collapses into a rejected promise (a readable 403 is a distinct
  // `forbidden_origin` outcome — do not merge the two), OR a client-side deadline
  // abort (sign-out-flow.ts SIGN_OUT_LOGOUT_TIMEOUT_MS — an AbortError rejects the fetch
  // and lands in this same `{ error }` bucket). T8 auto-retries this. For the
  // abort case that retry can MASK a finding: a slow-but-SUCCEEDING /logout that
  // the server already processed, aborted at the 10s deadline, becomes
  // abort -> `network` -> T8 retry -> the retried POST has no cookie -> it no-ops
  // to a bare 200 -> `signed_out` — the exact masking the `session_still_live`
  // note below warns about, reached here via a timeout rather than a lying 200.
  | 'network'
  // Readable 403. handleLogout returns this for every origin/audience/session
  // ownership check, so any HANDLER-generated 403 from /logout lands here. T8
  // never retries this. (An edge 403 from CloudFront/API Gateway/WAF also lands
  // here and mislabels the reason; see classifySignOutResponse — the verdict
  // stays correct, only the label degrades.)
  | 'forbidden_origin'
  // 400. handleLogout clears the device cookie but does NOT end the session
  // (ambiguous/invalid refresh cookie). T8 never retries this.
  | 'invalid_request'
  // Retriable by contract: any 5xx, plus 408 (request timeout) and 429 (too
  // many requests — API Gateway throttling). ENG-2713's future 503 lands here
  // unchanged. T8 auto-retries this bucket.
  | 'server_unavailable'
  // Any other non-2xx, a malformed status, or a resolved fetch that yielded a
  // non-`Response` (a client bug, not a network condition). T8 never retries
  // this — a throw/garbage is deterministic, so retrying is waste.
  //
  // T8 NOTE (observability): a CODING DEFECT in either classifier emits NOTHING
  // at runtime — both classifiers resolve, never throw — so it is
  // indistinguishable from hostile input as it happens. The fail-quiet posture
  // is safe ONLY for a defect that manifests as a THROW: the try/catch converts
  // it to `reason: 'unexpected'`, i.e. a VISIBLE sign-out failure, never a false
  // `signed_out`. A NON-throwing defect is NOT covered — an inverted brand check
  // or a broken `redirected === false` comparison still returns from the happy
  // path and can produce a false `signed_out`, which is exactly what the F6 test
  // row exists to catch at build time, before it ships. So T8 must ALARM ON THE
  // RATE of `reason: 'unexpected'`; do not wait for an exception to surface the
  // defect, because there is no exception. And mind the direction: such a
  // regression makes the `unexpected` rate FALL (its cases become `signed_out`),
  // not rise — so a falling rate is not, by itself, evidence of health.
  | 'unexpected'
  // /logout returned a bare 200 but the GET /userinfo probe PROVED the session
  // was still live (a 200 naming our own sessionId). NOT client-remediable and
  // NEVER auto-retried: the 200 already cleared the refresh cookie, so a retried
  // POST /logout has no credential, no-ops to 200, and would MASK the finding by
  // flipping the screen back to signed_out. Surfaced to the user; closure is
  // server-side (ENG-2713).
  | 'session_still_live';

/**
 * How much the GET /userinfo confirmation probe actually managed to check.
 *
 * This is MANDATORY on the `signed_out` verdict precisely so no consumer can
 * read a sign-out as "confirmed revoked" when it was only "accepted and not
 * disproven". The distinction is the entire reason ENG-2907 exists.
 *
 *   - `not_run`      — /logout returned a bare 200 but no probe was performed
 *                      (the probe is a T3/T8 decision; this is the default the
 *                      pure /logout classifier emits).
 *   - `no_token`     — /logout returned a bare 200 but no in-memory access token
 *                      was held to probe with, so the probe could not run.
 *   - `token_expired`— probe returned 401 while the held token was already at or
 *                      within 30 s of its own `exp`: a 401 is ordinary expiry,
 *                      not evidence of revocation.
 *   - `unauthorized` — probe returned 401 while the held token was comfortably
 *                      unexpired. This does NOT prove revocation (see below) and
 *                      is the DOCUMENTED RESIDUAL ENG-2713 closes.
 *   - `unavailable`  — probe returned 5xx (a SigningKeyError yields 503; see
 *                      below). The probe could not check; sign-out proceeds.
 *   - `failed`       — probe rejected (an aborted fetch included — the CALLER
 *                      owns the deadline; this classifier has NO internal
 *                      timeout, see the note on `classifyProbeResponse`),
 *                      returned a non-JSON body, or a 200 that did NOT name our
 *                      sessionId (captive portal / edge interstitial). It also
 *                      covers a malformed `held` (non-object, or a non-string /
 *                      empty sessionId) that cannot be matched against any body,
 *                      and a probe 401 whose held `exp` (or `nowMs`) is not a
 *                      finite, positive epoch-SECONDS value — NaN/Infinity, <= 0,
 *                      or an out-of-range magnitude such as milliseconds
 *                      (`>= 1e11`) — which fails toward this WEAK label rather
 *                      than the strong `unauthorized` (F4a/N4).
 *                      The probe could not check; sign-out proceeds.
 *
 * `not_run` and `no_token` are produced by the caller WITHOUT calling the probe;
 * the other four come out of `classifyProbeResponse`.
 */
export type SignOutProbeObservation =
  | 'not_run'
  | 'no_token'
  | 'token_expired'
  | 'unauthorized'
  | 'unavailable'
  | 'failed';

/**
 * The outcome of a sign-out attempt.
 *
 * `verdict: 'signed_out'` means — AS SHIPPED TODAY (T3) — ONLY "/logout returned
 * a bare 200", nothing more: T3 runs no probe, so it always pairs this verdict
 * with `probe: 'not_run'`. It is NOT "confirmed revoked" (the server's
 * verify-failure path also returns a bare 200 without revoking). Once T8 wires
 * the GET /userinfo confirmation probe, the same verdict widens to "/logout
 * returned a bare 200 AND the probe did not disprove it", and the `probe` field
 * then records how much was actually checked. The mandatory `probe` field is
 * present precisely so the type never says more than it knows. `revoked: true` —
 * a bare boolean that read as "accepted and not disproven" while looking like
 * "confirmed" — was the precise overclaim v2 renames away.
 *
 * `verdict: 'not_signed_out'` carries a machine-readable `reason` and, where the
 * wire produced one, the HTTP `status`.
 */
export type SignOutResult =
  | { verdict: 'signed_out'; probe: SignOutProbeObservation }
  | { verdict: 'not_signed_out'; reason: SignOutFailureReason; status?: number };

/**
 * What a GET /userinfo confirmation-probe response tells us, in probe-observation
 * space. This four-branch assembly is a T8 responsibility, NOT T3's: T3 (as
 * shipped) never calls `classifyProbeResponse` and always emits
 * `probe: 'not_run'`. When T8 wires the probe in, its caller owns FOUR branches
 * as it assembles the final `SignOutResult` — the two this classifier can emit,
 * plus two the caller decides itself WITHOUT ever calling the probe:
 *
 *   - `{ inconclusive, observation }` -> { verdict: 'signed_out', probe: observation }
 *   - `session_live`                  -> { verdict: 'not_signed_out',
 *                                          reason: 'session_still_live' }
 *   - probe not attempted (bare 200, T8's caller chose not to probe)
 *                                     -> { verdict: 'signed_out', probe: 'not_run' }
 *   - no in-memory token to probe with
 *                                     -> { verdict: 'signed_out', probe: 'no_token' }
 *
 * The last two never come out of `classifyProbeResponse` (its `inconclusive` arm
 * is typed to EXCLUDE them); they are the caller's own pre-probe decisions and
 * are listed here so the T8 author implementing from this table does not forget
 * to emit `no_token`.
 *
 * The mapping is deliberately the caller's job: the probe classifier observes,
 * it does not decide the sign-out verdict. Only `session_live` (a 200 naming our
 * own sessionId) downgrades a bare-200 sign-out; every other reading is
 * `inconclusive` — it keeps the `signed_out` verdict and merely records the
 * observation. The `kind` names the EPISTEMIC status (`session_live` = something
 * was found, `inconclusive` = nothing dispositive was), never the finding.
 */
export type SignOutProbeClassification =
  | { kind: 'session_live' }
  | {
      kind: 'inconclusive';
      observation: Exclude<SignOutProbeObservation, 'not_run' | 'no_token'>;
    };

/**
 * Classify a completed POST /logout `Response`, or the error a rejected fetch
 * threw. Returns the `SignOutResult` the T3 `signOut()` caller acts on BEFORE it
 * wipes local state.
 *
 * STATUS-ONLY, and SYNCHRONOUS by design. The caller needs the verdict before
 * the local session wipe and cannot `await` a body read at that point, so this
 * function never calls `response.json()`. Nothing it returns is derived from the
 * body or the fetch-rejection value (a `network`/`server_unavailable` outage
 * message carrying host:port would otherwise leak into the T8 UI — the
 * client-side analogue of ENG-2709's server-side constant-description work).
 * A bare 200 yields `probe: 'not_run'`: this classifier ran no probe, and says
 * exactly that.
 *
 * WHAT IS GUARANTEED against a hostile/lying `fetchImpl`: `signOut` injects
 * `fetchImpl` (client.ts:139, typed `typeof fetch`); a test double, MSW handler
 * or tracing wrapper can resolve `undefined`, `null`, a primitive, or an object
 * whose `response`/`status`/`redirected` are the wrong type or throw from a
 * getter — values TypeScript cannot catch at that boundary. The whole body runs
 * inside a single try/catch (enforced BY CONSTRUCTION, matching
 * `classifyProbeResponse`, not by hand-placed guards per property), so for EVERY
 * such input — INCLUDING a throwing `response` getter, which used to escape —
 * this function returns a `SignOutResult` and never throws (a throw here, before
 * the wipe, would strand the caller signed-in-looking-but-not — the worst
 * failure on this path), and it never produces a false `verdict: 'signed_out'`
 * from an ACCIDENTALLY duck-typed value (the brand check below draws that line —
 * and names its own limit against a HOSTILE one).
 * The only `signed_out` path requires `'error'` to be absent, `status` to be the
 * integer `200`, `response.redirected === false`, AND `res` to answer the
 * `[object Response]` brand (via `Object.prototype.toString` — which survives
 * cross-realm iframe/worker responses, unlike `instanceof Response`). That set is
 * satisfied by every genuine `Response` construction path (`new Response`,
 * `Response.json`, `.clone()`, real fetch, cloned fetch) and by no ORDINARY
 * duck-typed `{ status: 200, redirected: false }` literal (its brand is
 * `[object Object]`).
 *
 * The brand check is a SHAPE check against ACCIDENTAL duck-typing — a test
 * double, MSW handler or tracing wrapper that returns a plain object — NOT an
 * authenticity check. It is FORGEABLE: `Object.prototype.toString` reads
 * `Symbol.toStringTag`, so
 * `{ status: 200, redirected: false, [Symbol.toStringTag]: 'Response' }`
 * classifies as `signed_out`. That is not a hole worth closing: any code that can
 * set that symbol on the resolved value can also inject `fetchImpl`
 * (client.ts:139), and such code already controls the outcome outright. The check
 * earns its keep by rejecting the UNINTENTIONAL `{ status: 200 }` literal, not a
 * hostile one.
 *
 * `network` is returned ONLY for the `{ error }` branch (an actual fetch
 * rejection). A resolved fetch that yielded a non-`Response`, or a malformed
 * status, is a CLIENT bug — classified `unexpected`, never `network`, so the UI
 * does not auto-retry it five times and then tell the user "you appear to be
 * offline".
 *
 * The status->reason mapping is exact for HANDLER-generated responses
 * (handleLogout pairs 403 with `forbidden_origin`, 400 with `invalid_request`).
 * It is best-effort for a status injected by the edge (a CloudFront/API
 * Gateway/WAF 403 never reaches the Lambda): the verdict stays correct, but the
 * `reason` label may not match the true cause. The body carries no signal the
 * status does not for the handler's own responses.
 */
export function classifySignOutResponse(
  input: { response: Response } | { error: unknown },
): SignOutResult {
  // The ENTIRE body is wrapped so no throw can escape into signOut() before the
  // local wipe — including a throwing `response`/`status`/`redirected` getter on
  // a hostile double. A throw is deterministic garbage -> `unexpected`, and the
  // catch returns it WITHOUT a status field even on paths where a valid integer
  // `status` was already read (e.g. a throwing `redirected` getter after an
  // integer 200): once any getter on `res` throws, the whole response shape is
  // untrustworthy, so a status read off it is dropped DELIBERATELY rather than
  // reported.
  try {
    // Whole input is not an object (a lying `fetchImpl` or untyped caller handed
    // us null/undefined/a primitive): a client bug, not a network condition.
    if (input === null || typeof input !== 'object') {
      return { verdict: 'not_signed_out', reason: 'unexpected' };
    }
    // `network` is the fetch-rejection branch ONLY, keyed on the `error`
    // discriminant the caller constructs — never inferred from a bad response.
    if ('error' in input) {
      return { verdict: 'not_signed_out', reason: 'network' };
    }
    const res = (input as { response?: unknown }).response;
    // A resolved fetch that yielded no usable `Response` object is a client bug.
    if (res === null || typeof res !== 'object') {
      return { verdict: 'not_signed_out', reason: 'unexpected' };
    }
    const status = (res as { status?: unknown }).status;
    // A non-integer status ("200", [200], { valueOf }, NaN, Infinity) can never
    // match the contract; classify as unexpected WITHOUT a status field so
    // nothing non-integer reaches SignOutResult.status (NaN/Infinity
    // JSON-serialise to null and would render as garbage in the T8 UI).
    if (!Number.isInteger(status)) {
      return { verdict: 'not_signed_out', reason: 'unexpected' };
    }
    const code = status as number;
    // Accept EXACTLY the one success shape handleLogout emits: a bare, integer
    // 200 on a GENUINE Response. Not a 2xx class — authFetch (client.ts) sets no
    // `redirect`, so a followed redirect chain's final 200 would otherwise be
    // accepted. `redirected` is checked with strict `=== false` (a
    // followed-redirect Response reports `true`; a duck-typed object with no
    // `redirected` reports `undefined`; both must fail). The brand check
    // (`[object Response]`, cross-realm-safe unlike `instanceof`) rejects a
    // duck-typed `{ status: 200, redirected: false }` literal that would
    // otherwise satisfy every other test.
    if (code === 200) {
      const redirected = (res as { redirected?: unknown }).redirected;
      if (
        redirected === false &&
        Object.prototype.toString.call(res) === '[object Response]'
      ) {
        return { verdict: 'signed_out', probe: 'not_run' };
      }
      return { verdict: 'not_signed_out', reason: 'unexpected', status: 200 };
    }
    if (code === 400) {
      return { verdict: 'not_signed_out', reason: 'invalid_request', status: code };
    }
    if (code === 403) {
      return { verdict: 'not_signed_out', reason: 'forbidden_origin', status: code };
    }
    // 408/429 are the canonical "retry me" signals (429 = API Gateway
    // throttling); route them to the retriable bucket so T8 backs off.
    if (code === 408 || code === 429) {
      return { verdict: 'not_signed_out', reason: 'server_unavailable', status: code };
    }
    if (code >= 500 && code < 600) {
      return { verdict: 'not_signed_out', reason: 'server_unavailable', status: code };
    }
    return { verdict: 'not_signed_out', reason: 'unexpected', status: code };
  } catch {
    return { verdict: 'not_signed_out', reason: 'unexpected' };
  }
}

/** Proactive-expiry skew for interpreting a probe 401. A held token within this
 *  many ms of its own `exp` is treated as expired, matching the silent-refresh
 *  lead time (silent-refresh.ts:13, LEAD_TIME_MS). */
const PROBE_EXP_SKEW_MS = 30_000;

/**
 * Classify a GET /userinfo confirmation-probe response, or the error a rejected
 * probe fetch threw, into probe-observation space.
 *
 * ASYNC AND BODY-READING, unlike `classifySignOutResponse` — and the asymmetry
 * is load-bearing, not an oversight, so DO NOT "fix" it to match the other
 * classifier. The /logout classifier runs BEFORE the local wipe and must return
 * synchronously; this probe runs AFTER the wipe (the wipe must never be delayed
 * by a network call), so awaiting `response.json()` costs the caller nothing.
 * And it MUST read the body: on a 200 the response body's `session_id` is the
 * only thing that separates proof-the-session-is-live from a captive-portal /
 * edge-interstitial 200. `held.sessionId` is the id decoded from the access
 * token CAPTURED BEFORE THE WIPE (see PROBE PRECONDITION below) — NOT read from
 * memory at probe time, when the wipe has already nulled `currentAccessToken`.
 *
 * WHY A PROBE 401 IS NOT PROOF OF REVOCATION — the correction that drives this
 * whole design. A probe 401 is THREE-WAY ambiguous: revoked / ordinary token
 * expiry / revocation-store fault — and the third arm is CORRELATED WITH THE
 * LIE it would hide. `isDenied` (tokens/denylist.ts:103-120) is a bare
 * `await Promise.all(keys.map((k) => redis.get(k)))` with no catch, so a Redis
 * fault throws; `handleUserinfo` maps every non-`SigningKeyError` throw to
 * `401 invalid_token` with a constant description, and the auth-service pins it
 * (handler-userinfo-signing-key-transient.test.ts, test named `returns 401
 * invalid_token with a CONSTANT description on a Redis-style infra error`: an
 * ECONNREFUSED Redis fault -> `401 {error:'invalid_token'}`). So the SAME outage
 * that makes /logout fail open to a silent 200 makes /userinfo answer 401.
 *
 * Therefore: probe 200 (naming our sessionId) = PROOF the session is live, and
 * catches both the transient-blip lie and the wrong-audience-cookie lie; probe
 * 401 PROVES NOTHING -> `signed_out` with `probe: 'unauthorized'`, a DOCUMENTED
 * RESIDUAL and the sustained-outage blind spot ENG-2713 closes. A `SigningKeyError`
 * yields 503, which is why `'unavailable'` exists as its own observation — a
 * two-outcome (200/401) probe design would misread a signing-key blip as
 * revocation.
 *
 * ENG-2713 PROMOTION PRECONDITION: when 2713 maps store faults to 503, a probe
 * 401 may be promoted to proof-of-revocation — but that promotion must NOT rest
 * on client-clock evidence alone. `token_expired` here is decided against
 * `nowMs` (a CLIENT clock); a client clock running behind the server would call
 * a token that merely expired "still authorized" and, post-promotion, would
 * report "we confirmed your session was killed" for it. Any promotion must
 * anchor on a server-asserted clock or a server-side signal, never this
 * classifier's local `exp` comparison. (This design chain already got the 401
 * claim wrong once; this note is what stops it recurring.)
 *
 * PROBE PRECONDITION — the transport MUST carry TWO things, and BOTH are
 * destroyed by the local wipe this classifier runs after. Get either wrong and
 * every probe 401s -> `unauthorized`, an EXPECTED value, so NO test goes red and
 * the proof-of-live detector is PERMANENTLY BLIND while reporting success. After
 * ENG-2713 promotes `unauthorized` to proof, a blind probe becomes a false
 * "we confirmed your session was killed" on every sign-out.
 *
 *   (1) THE BEARER. `handleUserinfo` reads ONLY the bearer
 *       (services/auth-service/src/handler.ts:169:
 *       `if (!bearer) return err(401, 'invalid_token', ...)`);
 *       `credentials: 'include'` does NOT substitute for it. But the local wipe
 *       nulls `currentAccessToken`, and `authFetch` only auto-attaches the bearer
 *       WHEN ONE IS STILL HELD
 *       (`if (currentAccessToken && !headers.has("authorization"))`,
 *       client.ts:393). AFTER the wipe there is none to attach. So T8 MUST
 *       capture `currentAccessToken` — plus the decoded `sessionId`/`exp` for
 *       `held` — BEFORE the wipe and AFTER `scheduler.cancel()` (capturing before
 *       the cancel leaves a window where the silent-refresh scheduler can swap
 *       `currentAccessToken` between capture and cancel), and pass the captured
 *       token as an EXPLICIT `authorization` header on the probe.
 *   (2) THE AUDIENCE. /userinfo resolves audience via `extractAudience`
 *       (services/auth-service/src/http_helpers.ts:147-156), which DEFAULTS to
 *       `clearpath-main` when neither `?audience=` nor the `X-Molaris-Audience`
 *       header is present. A probe that does not pin the audience verifies an
 *       admin/vendor/portal/account token against `clearpath-main` and 401s the
 *       same silent way. This is the trap `handleLogout` already documents.
 *
 * Routing through `authFetch` is NECESSARY BUT NOT SUFFICIENT. `authFetch` sets
 * `x-molaris-audience: config.audience` (client.ts:421-422), so it covers (2)
 * for free. It does NOT cover (1) after the wipe: its bearer auto-attach is
 * guarded on a still-held `currentAccessToken` (client.ts:393) that the wipe has
 * already nulled. The SAME guard is what lets you supply the captured bearer
 * yourself — `!headers.has("authorization")` means a caller-set `authorization`
 * header is honoured untouched. So the correct T8 call is: route through
 * `authFetch` AND set the pre-wipe-captured bearer explicitly, e.g.
 * `authFetch('/userinfo', { headers: { authorization: 'Bearer ' + captured },
 * signal })`. "Route through `authFetch`" ALONE ships a permanently blind probe.
 *
 * NO INTERNAL TIMEOUT — the CALLER owns the deadline. This classifier keeps pure
 * and never races a clock; `await response.json()` is unbounded here. T8 MUST
 * pass an `AbortSignal` to the probe fetch (an aborted fetch rejects, which this
 * already handles as `failed`). Without one, a `json()` that never settles would
 * hang `signOut()` — and the awaiting UI — on "Signing out…" forever.
 *
 * ASYMMETRY WITH `classifySignOutResponse` (deliberate, not an oversight): this
 * classifier ignores `redirected` entirely, while the /logout classifier
 * requires `redirected === false`. A followed-redirect /userinfo would not name
 * our sessionId in a JSON body, so it fails the body check and lands on `failed`
 * regardless — there is nothing for a `redirected` gate to add here.
 *
 * NEVER THROWS: it runs after the wipe but still inside `signOut()`, which by
 * contract never rejects. Any hostile input (garbage argument, throwing getter,
 * non-JSON body, a `.json()` that is not a function, a malformed `held`)
 * resolves to `failed` — the observation that never blocks sign-out — never a
 * rejected promise. It never surfaces fetch-rejection detail (host:port); a
 * rejection resolves to a bare `failed`.
 *
 * @param input The probe outcome: a completed `Response`, or the fetch rejection.
 * @param held  The sessionId decoded from the held access token, plus its `exp`
 *              in epoch SECONDS (Session.expiresAt, decoded by
 *              sessionFromAccessToken in client.ts). A malformed `held`
 *              (non-object, or a non-string / empty sessionId) can match no real
 *              body and yields `failed` before anything is read — see B2. An `exp`
 *              that is not a finite, positive epoch-SECONDS value — NaN/Infinity,
 *              <= 0, or an out-of-range magnitude such as milliseconds
 *              (`>= 1e11`) — likewise yields `failed` on a probe 401, failing
 *              toward the WEAK label instead of `unauthorized` (F4a/N4).
 * @param nowMs The current time in epoch MILLISECONDS (Date.now()), injected so
 *              the classifier stays pure and deterministic.
 */
export async function classifyProbeResponse(
  input: { response: Response } | { error: unknown },
  held: { sessionId: string; expiresAtSeconds: number },
  nowMs: number,
): Promise<SignOutProbeClassification> {
  try {
    // B2: refuse to read anything until `held` is a usable, non-empty string
    // sessionId. `sessionFromAccessToken` (client.ts) sets
    // `sessionId: claims.session_id ?? ""`, so a claimless token yields "".
    // (Symbol, not a line number — this citation has drifted twice.) Without
    // this guard an absent held sessionId (`undefined`) against a body whose OWN
    // `session_id` is also `undefined` compares `undefined === undefined` ->
    // `true` and would FABRICATE `session_live` — the loudest, never-retried
    // outcome — from garbage. `"" === ""` is the same trap for the empty case.
    // (The body-side hasOwnProperty guard below independently rejects an ABSENT
    // `session_id`; this held-side guard is what stops an OWN undefined/"" match.)
    const heldObj = held as unknown;
    if (heldObj === null || typeof heldObj !== 'object') {
      return { kind: 'inconclusive', observation: 'failed' };
    }
    const heldSessionId = (heldObj as { sessionId?: unknown }).sessionId;
    if (typeof heldSessionId !== 'string' || heldSessionId === '') {
      return { kind: 'inconclusive', observation: 'failed' };
    }
    // Fetch rejection (an aborted probe included) or a garbage argument: the
    // probe could not conclude. `failed` never blocks -> signOut still completes.
    if (input === null || typeof input !== 'object' || 'error' in input) {
      return { kind: 'inconclusive', observation: 'failed' };
    }
    const res = (input as { response?: unknown }).response;
    if (res === null || typeof res !== 'object') {
      return { kind: 'inconclusive', observation: 'failed' };
    }
    const status = (res as { status?: unknown }).status;
    // 401 proves nothing (see the doc comment). If the held token was itself at
    // or within the skew of its own `exp`, a 401 is ordinary expiry; otherwise
    // it is the documented residual that still resolves to signed_out.
    if (status === 401) {
      // F4a: garbage expiry data must fail toward the WEAK label. NaN/undefined
      // /Infinity expiry, or a NaN `nowMs`, make every comparison `false` and
      // would silently default to the STRONG `unauthorized`. Require a finite,
      // positive epoch-seconds `exp` and a finite `nowMs` before comparing.
      //
      // N4: also reject an `exp` in MILLISECONDS. `exp >= 1e11` (year ~5138 in
      // seconds; every real epoch-MS value is ~1e12+) means a caller passed ms
      // where seconds were required. Left through, `exp * 1000` is astronomically
      // large, the skew comparison is always false, and a genuinely-expired token
      // mislabels its 401 as the STRONG `unauthorized` instead of `token_expired`
      // — a false confirmation post-ENG-2713. Fail toward the WEAK label instead.
      const exp = held.expiresAtSeconds;
      if (!Number.isFinite(exp) || exp <= 0 || exp >= 1e11 || !Number.isFinite(nowMs)) {
        return { kind: 'inconclusive', observation: 'failed' };
      }
      const expired = nowMs + PROBE_EXP_SKEW_MS >= exp * 1000;
      return {
        kind: 'inconclusive',
        observation: expired ? 'token_expired' : 'unauthorized',
      };
    }
    // Any 5xx (a SigningKeyError yields 503): the probe could not check.
    if (typeof status === 'number' && status >= 500 && status < 600) {
      return { kind: 'inconclusive', observation: 'unavailable' };
    }
    if (status === 200) {
      // Read the body: only a 200 naming OUR sessionId is proof the session is
      // live. A different or missing session_id is a captive-portal / edge 200
      // and must not raise a scary "still signed in" screen. `heldSessionId` is
      // proven a non-empty string above, so an absent body `session_id`
      // (`undefined`) can never match it.
      const body: unknown = await (res as Response).json();
      // N3: only an OWN `session_id` counts. A parsed JSON body can never carry
      // an inherited key, but a lying `fetchImpl` whose `.json()` returns a
      // prototype-polluted object could — `hasOwnProperty` stops an INHERITED
      // `session_id` from contributing to `session_live`.
      if (
        body !== null &&
        typeof body === 'object' &&
        Object.prototype.hasOwnProperty.call(body, 'session_id') &&
        (body as { session_id?: unknown }).session_id === heldSessionId
      ) {
        return { kind: 'session_live' };
      }
      return { kind: 'inconclusive', observation: 'failed' };
    }
    // Any other status (or a non-integer/garbage one): could not conclude.
    return { kind: 'inconclusive', observation: 'failed' };
  } catch {
    // Non-JSON body, a throwing getter, or any other hostile input: the probe
    // could not conclude. Resolve to `failed`, never reject.
    return { kind: 'inconclusive', observation: 'failed' };
  }
}
